import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectExternalOwners,
  selectObjectTypesMap,
  selectObjectTypesTree,
} from 'slices/objectTypes.slice';
import type { TTypeTree } from 'utils/objectTypes';
import type { TType } from '@timeedit/types';
import { TEDrawerSection, TEInput } from '@timeedit/ui-components';

type TOrgNodeProps = Partial<TType & { parentIds?: number[] }>;

type TPropertiesProps = {
  value?: TOrgNodeProps;
  onChangeValue: (type: string, value: any) => void;
};

const extractOrgNode = (treeObj: TTypeTree, orgNode: TOrgNodeProps) => {
  if (orgNode?.id == null) {
    return [];
  }

  const ancestors = treeObj?.[orgNode.id]?.ancestors;
  if (Array.isArray(ancestors)) {
    return [...ancestors].reverse();
  }

  return orgNode.parentIds || [];
};

export const Properties = ({ value, onChangeValue }: TPropertiesProps) => {
  const mapObj = useSelector(selectObjectTypesMap);
  const treeObj = useSelector(selectObjectTypesTree);

  const aliasOptions = useMemo(() => {
    const objectNode = extractOrgNode(treeObj, value);
    const ancestorOptions = objectNode
      .map((id: number) => mapObj?.[id])
      .filter((obj) => !!obj)
      .map((obj) => ({
        label: obj.name,
        value: obj.id,
      }));

    return [{ label: 'None', value: 0 }, ...ancestorOptions];
  }, [value?.id, value?.parentIds, mapObj, treeObj]);

  const aliasTypeValue = useMemo(() => {
    if (value?.aliasType === value?.id) return 0;
    return value?.aliasType;
  }, [value?.aliasType, value?.id]);

  // selectExternalOwners
  const optionsExternalOwners = useSelector(selectExternalOwners);

  return (
    <TEDrawerSection label="Properties">
      <div className="wrapper-section">
        <div className="section-item">
          <TEInput.Switch
            checked={value.allowStandardObjects}
            onChange={(val) => onChangeValue('allowStandardObjects', val)}
            label="Allow standard objects"
            tooltip="Allow standard objects"
            data-testid="standard-object"
          />
          <TEInput.Switch
            checked={value.allowAbstractObjects}
            onChange={(val) => onChangeValue('allowAbstractObjects', val)}
            label="Allow abstract objects"
            tooltip="Allow abstract objects"
            data-testid="abstract-object"
          />
          <TEInput.Switch
            checked={value.allowVirtualObjects}
            onChange={(val) => onChangeValue('allowVirtualObjects', val)}
            label="Allow virtual objects"
            tooltip="Allow virtual objects"
            data-testid="virtual-object"
          />
          <TEInput.Switch
            checked={value.personType}
            onChange={(val) => onChangeValue('personType', val)}
            label="Person type"
            tooltip="Person type"
            data-testid="person-type"
          />
          <TEInput.Switch
            checked={value.locationType}
            onChange={(val) => onChangeValue('locationType', val)}
            label="Location type"
            tooltip="Location type"
            data-testid="location-type"
          />
        </div>
        <div className="section-item">
          <TEInput.Select
            value={aliasTypeValue}
            onChange={(val) => onChangeValue('aliasType', val ?? 0)}
            label="Alias type"
            tooltip="Alias type"
            placeholder="Select"
            options={aliasOptions}
            data-testid="alias-type"
          />
          <TEInput.Select
            label="External ownership"
            placeholder="Select"
            tooltip="External ownership"
            value={value.externalOwner}
            onChange={(val) => onChangeValue('externalOwner', val)}
            options={optionsExternalOwners}
            data-testid="external-ownership"
          />
          <TEInput.Switch
            checked={value.skipLastReserved}
            onChange={(val) => onChangeValue('skipLastReserved', val)}
            label={`Don't use "Recently used"`}
            tooltip={`Don't use "Recently used"`}
            data-testid="skipLastReserved"
          />
        </div>
      </div>
    </TEDrawerSection>
  );
};
